// @ts-nocheck

import { filter, pick } from 'lodash';
import { compose } from 'recompose';
import { createSelector } from 'reselect';

import { SpecialFilterValues } from '../../constants/livebar';
import { getSelectedSportIdFromProps } from '../../selectors/_propsGetters_';
import { selectEntitiesCategories } from '../../selectors/entities/categories/entitiesCategoriesPrimary.selector';
import { selectEntitiesMatches } from '../../selectors/entities/matches/entitiesMatchesPrimary.selector';
import {
  selectEntitiesMultiMatches,
  selectEntitiesMultiState,
} from '../../selectors/entities/multi/entitiesMultiPrimary.selector';
import { selectEntitiesSportIds } from '../../selectors/entities/sportIds/entitiesSportIdsPrimary.selector';
import { selectEntitiesSports } from '../../selectors/entities/sports/entitiesSportsPrimary.selector';
import { selectEntitiesTournaments } from '../../selectors/entities/tournaments/entitiesTournamentsPrimary.selector';
import {
  selectMultiviewList,
  selectMultiviewScrollToHeader,
} from '../../selectors/multiview/multiviewPrimary.selector';
import { selectRuntimeIsWTVEnabled } from '../../selectors/runtime/runtimePrimary.selector';
import { selectUserPrefsShowWTV } from '../../selectors/user/userPrimary.selector';
import { type MatchState } from '../../states/entities/matches';
import { MatchEntity } from '../../states/entities/matches';
import { type FacetType } from '../../types/facets';
import { type Selector } from '../../types/selectors';
import { getFacetsSports } from '../../utils/facets';

const emptyArray: [] = [];

const filterWinamaxTv = (sportId) => (matches) =>
  filter(matches, (match) =>
    sportId === SpecialFilterValues.WINAMAX_TV_FILTER
      ? MatchEntity.isWinamaxTvChannel(match)
      : sportId
        ? match.sportId === sportId
        : true,
  );
const removeFalseValue = (matches) => filter(matches, (match) => (match ? true : false));
const removeEndedMatch = (matches) => filter(matches, (match) => !MatchEntity.isEnded(match));
const pickOnlyRequiredProp = (matches) =>
  matches.map((x) => pick(x, ['matchId', 'sportId', 'categoryId', 'tournamentId', 'title', 'tvChannels']));

const generateListBySport = (list, sportId) =>
  compose(pickOnlyRequiredProp, filterWinamaxTv(sportId), removeEndedMatch, removeFalseValue)(list);

const selectSimpleList: Selector<Array<number>> = createSelector(
  [selectEntitiesMultiMatches, selectEntitiesMatches],
  (multiMatches, entitiesMatches) => {
    if (multiMatches && entitiesMatches) {
      return multiMatches.map((x) => entitiesMatches[x]);
    }
    return emptyArray;
  },
);

const selectGroups: Selector<Array<unknown>> = createSelector(
  [selectEntitiesCategories, selectEntitiesTournaments, getSelectedSportIdFromProps, selectSimpleList],
  (entitiesCategories, entitiesTournaments, selectedSportId, list) => {
    const filteredList = generateListBySport(list, selectedSportId);

    return filteredList.reduce((acc, match) => {
      let { categoryId, tournamentId }: { categoryId: number; tournamentId: number } = match ?? {};

      const currGroup = acc[tournamentId];

      if (currGroup) {
        categoryId = currGroup.categoryId ? currGroup.categoryId : match.categoryId;
        tournamentId = currGroup.tournamentId ? currGroup.tournamentId : match.tournamentId;
      }

      const categoryName = entitiesCategories?.[categoryId]?.categoryName ?? '';
      const tournamentName = entitiesTournaments?.[tournamentId]?.tournamentName ?? '';
      const name = `${categoryName} - ${tournamentName}`;

      return {
        ...acc,
        [tournamentId]: currGroup
          ? {
              ...currGroup,
              name,
              items: [...currGroup.items, match],
            }
          : {
              name,
              items: [match],
            },
      };
    }, {});
  },
);

const selectFacetsSports: Selector<Array<FacetType>> = createSelector(
  [selectEntitiesSports, getSelectedSportIdFromProps, selectSimpleList, selectEntitiesSportIds],
  (entitiesSports, selectedSportId, list, sportIds) => {
    return getFacetsSports(list, sportIds, entitiesSports);
  },
);

const selectIsLoading: Selector<boolean> = createSelector([selectEntitiesMultiState], (multiMap) => !multiMap);

const selectIsNull: Selector<boolean> = createSelector(selectEntitiesMultiState, (multiMap) => multiMap === null);

type MultiviewModalBlockPickerPropsType = {
  isLoading: boolean;
  isNull: boolean;
  multiviewList: Array<MatchState>;
  isWTVEnabled: boolean;
  showWTV: boolean;
  scrollToHeader: boolean;
  facets: Array<FacetType>;
  selectedSportId?: number;
};

const selectMultiviewModalBlockPickerProps: Selector<MultiviewModalBlockPickerPropsType> = createSelector(
  [
    selectIsLoading,
    selectIsNull,
    selectGroups,
    selectMultiviewList,
    selectRuntimeIsWTVEnabled,
    selectUserPrefsShowWTV,
    selectMultiviewScrollToHeader,
    selectFacetsSports,
    getSelectedSportIdFromProps,
  ],
  (isLoading, isNull, groups, multiviewList, isWTVEnabled, showWTV, scrollToHeader, facets, selectedSportId) => ({
    isLoading,
    isNull,
    groups,
    multiviewList,
    isWTVEnabled,
    showWTV,
    scrollToHeader,
    facets,
    selectedSportId,
  }),
);

export default selectMultiviewModalBlockPickerProps;
